import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@aspnet/signalr';
import { interval, Subscription } from 'rxjs';
import { environment as env } from '@env/environment';
import { AuthenticationService } from '@app/core/services/AuthenticationService';
import { IpsosInvoiceImport, IpsosInvoiceImportRow, IpsosInvoiceImportService } from '@app/core/services/IpsosInvoiceImportService';

@Component({
  standalone: false,
  selector: 'app-ipsos-invoice-import',
  templateUrl: './ipsos-invoice-import.component.html',
  styleUrls: ['./ipsos-invoice-import.component.scss']
})
export class IpsosInvoiceImportComponent implements OnInit, OnDestroy {
  organizations: { Id: number; Name: string }[] = [];
  organizationSelection: { Id: number; Name: string } | string = '';
  organizationsLoading = false;
  organizationsLoaded = false;
  organizationLoadError = '';
  private userUpdateSubscription?: Subscription;
  get isAdmin(): boolean { return this.auth.userHasRole('admin'); }
  get selectedOrganizationId(): number | undefined {
    const selected = this.organizationSelection;
    return typeof selected === 'object' && this.organizations.some(o => o.Id === selected.Id)
      ? selected.Id : undefined;
  }
  displayOrganization(value: { Name: string } | string): string {
    return typeof value === 'string' ? value : value?.Name || '';
  }
  get filteredOrganizations(): { Id: number; Name: string }[] {
    const query = typeof this.organizationSelection === 'string' ? this.organizationSelection.trim().toLocaleLowerCase('sv-SE') : '';
    return this.organizations.filter(o => (o.Name || '').toLocaleLowerCase('sv-SE').includes(query));
  }
  imports: IpsosInvoiceImport[] = [];
  selected?: IpsosInvoiceImport;
  rows: IpsosInvoiceImportRow[] = [];
  uploadFile?: File;
  busy = false;
  selectedTabIndex = 0;
  historyLoading = true;
  rowsLoading = false;
  errorMessage = '';
  successMessage = '';
  progressText = '';
  processedRows = 0;
  totalRows = 0;
  displayedColumns = ['RowNumber', 'ExternalEmploymentNumber', 'MaskedPersonalNumber', 'Date', 'WorkedHours', 'GrossHourlySalary', 'CalculatedAmount', 'Errors'];
  private polling?: Subscription;
  private hub?: HubConnection;

  constructor(private service: IpsosInvoiceImportService, private auth: AuthenticationService, private cdr: ChangeDetectorRef) { }

  get organizationName(): string {
    return this.isAdmin ? '' : this.auth.userData?.User?.Organization?.Company?.Name || '';
  }

  ngOnInit(): void {
    this.loadOrganizations();
    this.userUpdateSubscription = this.auth.onUserUpdate().subscribe(() => this.loadOrganizations());
    this.refresh();
    this.polling = interval(10000).subscribe(() => this.refreshSelected());
    this.connectSignalR();
  }

  ngOnDestroy(): void { this.userUpdateSubscription?.unsubscribe(); this.polling && this.polling.unsubscribe(); this.hub && this.hub.stop(); }

  loadOrganizations(): void {
    if (!this.isAdmin || this.organizationsLoading || this.organizationsLoaded) return;
    this.organizationsLoading = true;
    this.organizationLoadError = '';
    this.service.organizations().subscribe(items => {
      this.organizations = items;
      this.organizationsLoaded = true;
      this.organizationsLoading = false;
      this.cdr.markForCheck();
    }, () => {
      this.organizationsLoading = false;
      this.organizationLoadError = 'Organisationerna kunde inte hämtas. Kontrollera att API:t är uppdaterat och försök igen.';
      this.cdr.markForCheck();
    });
  }

  chooseFile(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length ? input.files[0] : undefined;
    this.errorMessage = '';
    this.successMessage = '';

    if (!file) {
      this.uploadFile = undefined;
      return;
    }

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.errorMessage = 'Välj en fil i CSV-format.';
      this.uploadFile = undefined;
      input.value = '';
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      this.errorMessage = 'Filen får vara högst 10 MB.';
      this.uploadFile = undefined;
      input.value = '';
      return;
    }

    this.uploadFile = file;
  }

  clearFile(input?: HTMLInputElement): void {
    this.uploadFile = undefined;
    this.errorMessage = '';
    if (input) input.value = '';
  }

  upload(): void {
    if (!this.uploadFile || this.busy) return;
    if (this.isAdmin && !this.selectedOrganizationId) {
      this.errorMessage = 'Välj en organisation i listan innan du laddar upp en fil.';
      return;
    }
    const uploadedFile = this.uploadFile;
    this.busy = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.service.upload(this.uploadFile, this.isAdmin ? this.selectedOrganizationId : undefined).subscribe(item => {
      this.busy = false;
      this.uploadFile = undefined;
      this.selectedTabIndex = 1;
      this.successMessage = 'Filen är uppladdad och har köats för validering.';
      this.select({ ...item, OriginalFileName: item.OriginalFileName || uploadedFile.name });
      this.refresh();
      this.cdr.markForCheck();
    }, error => {
      this.busy = false;
      this.errorMessage = this.getErrorMessage(error, 'Filen kunde inte laddas upp. Försök igen.');
      this.cdr.markForCheck();
    });
  }

  select(item: IpsosInvoiceImport): void {
    this.selectedTabIndex = 1;
    const previousId = this.selected?.Id;
    this.selected = item;
    this.rows = [];
    this.rowsLoading = true;
    this.errorMessage = '';
    this.progressText = '';
    this.processedRows = 0;
    this.totalRows = item.TotalRows;
    if (previousId && previousId !== item.Id) this.hub && this.hub.invoke('Unsubscribe', previousId).catch(() => undefined);
    this.hub && this.hub.invoke('Subscribe', item.Id).catch(() => undefined);
    this.service.rows(item.Id).subscribe(rows => {
      this.rows = rows;
      this.rowsLoading = false;
      this.cdr.markForCheck();
    }, error => {
      this.rowsLoading = false;
      this.errorMessage = this.getErrorMessage(error, 'Filen kan inte hämtas.');
      this.cdr.markForCheck();
    });
  }

  approve(): void {
    if (!this.selected || this.selected.Status !== 3 || this.busy) return;
    this.busy = true;
    this.errorMessage = '';
    this.successMessage = '';
    this.service.approve(this.selected).subscribe(item => {
      this.busy = false;
      this.selected = { ...this.selected, ...item };
      this.successMessage = 'Importen är godkänd och fakturan skapas.';
      this.refresh();
      this.cdr.markForCheck();
    }, error => {
      this.busy = false;
      this.errorMessage = this.getErrorMessage(error, 'Importen kunde inte godkännas.');
      this.cdr.markForCheck();
    });
  }

  openSource(): void {
    if (!this.selected) return;
    this.service.source(this.selected.Id).subscribe(blob => {
      const url = URL.createObjectURL(blob); window.open(url, '_blank', 'noopener'); setTimeout(() => URL.revokeObjectURL(url), 60000);
    }, error => {
      this.errorMessage = this.getErrorMessage(error, 'Originalfilen kunde inte öppnas.');
      this.cdr.markForCheck();
    });
  }

  refresh(): void {
    this.historyLoading = true;
    this.service.list().subscribe(items => {
      this.imports = items;
      this.historyLoading = false;
      if (this.selected) {
        const updated = items.find(i => i.Id === this.selected.Id);
        if (updated) this.selected = updated;
      }
      this.cdr.markForCheck();
    }, error => {
      this.historyLoading = false;
      this.errorMessage = this.getErrorMessage(error, 'Tidigare importer kunde inte hämtas.');
      this.cdr.markForCheck();
    });
  }

  refreshSelected(): void {
    if (!this.selected || this.selected.Status === 6 || this.selected.Status === 7 || this.selected.Status === 2) return;
    this.service.get(this.selected.Id).subscribe(item => {
      const statusChanged = !this.selected || item.Status !== this.selected.Status;
      this.selected = item;
      if (statusChanged && (item.Status === 2 || item.Status === 3)) this.service.rows(item.Id).subscribe(rows => {
        this.rows = rows;
        this.cdr.markForCheck();
      });
      this.cdr.markForCheck();
    });
  }

  statusName(status: number): string {
    return ['Väntar på kontroll', 'Validerar', 'Validering misslyckades', 'Redo för godkännande', 'Väntar på att skapa faktura', 'Skapar faktura', 'Klar', 'Misslyckades'][status] || 'Okänd';
  }

  statusTone(status: number): string {
    if (status === 6) return 'success';
    if (status === 2 || status === 7) return 'error';
    if (status === 3) return 'ready';
    return 'active';
  }

  statusKicker(status: number): string {
    if (status === 6) return 'Importen är slutförd';
    if (status === 2 || status === 7) return 'Importen behöver åtgärdas';
    if (status === 3) return 'Ditt underlag är redo';
    return 'Bearbetning pågår';
  }

  statusTitle(item: IpsosInvoiceImport): string {
    const status = item.Status;
    if (status === 0) return 'Väntar på validering';
    if (status === 1) return 'Kontrollerar filens rader';
    if (status === 2) return 'Valideringen innehåller fel';
    if (status === 3) return 'Redo att skapa samlingsfaktura';
    if (status === 4) return 'Fakturan väntar på att skapas';
    if (status === 5) return 'Samlingsfakturan skapas';
    if (status === 6) return 'Klart! Samlingsfakturan är skapad';
    if (status === 7) return item.ValidatedAt ? 'Fakturan kunde inte skapas' : 'Valideringen kunde inte slutföras';
    return 'Importstatus okänd';
  }

  statusDescription(item: IpsosInvoiceImport): string {
    if (item.Status === 0) return 'Filen är mottagen och kommer att kontrolleras automatiskt.';
    if (item.Status === 1) return 'Anställningar, personuppgifter, perioder och belopp kontrolleras rad för rad.';
    if (item.Status === 2) return 'Kontrollera felen i tabellen nedan, rätta filen och ladda sedan upp den igen.';
    if (item.Status === 3) return 'Alla rader är godkända. Granska totalen och starta fakturaskapandet när du är redo.';
    if (item.Status === 4) return 'Ditt godkännande är sparat. Fakturan skapas automatiskt. Du kan lämna sidan under tiden.';
    if (item.Status === 5) return 'Fakturaraderna skapas och kopplas till en ny samlingsfaktura. Du kan lämna sidan under tiden.';
    if (item.Status === 6) return item.InvoiceId
      ? `Importen är färdig och samlingsfaktura ${item.InvoiceId} är sparad.`
      : 'Importen är färdig och samlingsfakturan är sparad.';
    if (item.Status === 7) return 'Inga fler automatiska försök görs. Läs felmeddelandet nedan och försök igen när felet är åtgärdat.';
    return 'Status kunde inte tolkas. Uppdatera sidan och försök igen.';
  }

  stepState(step: number, item: IpsosInvoiceImport): string {
    const status = item.Status;
    if (step === 1) return 'complete';
    if (step === 2) {
      if (status === 2 || (status === 7 && !item.ValidatedAt)) return 'error';
      if (status >= 3) return 'complete';
      return 'active';
    }
    if (status === 7) return item.ValidatedAt ? 'error' : 'pending';
    if (status === 6) return 'complete';
    if (status >= 4) return 'active';
    return 'pending';
  }

  stepIcon(step: number, item: IpsosInvoiceImport): string {
    const state = this.stepState(step, item);
    if (state === 'complete') return 'check';
    if (state === 'error') return 'close';
    if (state === 'active') return 'more_horiz';
    return 'circle';
  }

  isProcessing(status: number): boolean {
    return status === 0 || status === 1 || status === 4 || status === 5;
  }

  fileSize(size: number): string {
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} kB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  }

  trackImport(_: number, item: IpsosInvoiceImport): string {
    return item.Id;
  }

  private connectSignalR(): void {
    const apiBase = env.hostApi.replace(/\/api\/?$/, '');
    this.hub = new HubConnectionBuilder().withUrl(`${apiBase}/hubs/import-progress`, { accessTokenFactory: () => this.auth.token }).configureLogging(LogLevel.Warning).build();
    this.hub.on('ImportProgress', event => {
      const importId = event.ImportId || event.importId;
      if (!this.selected || importId !== this.selected.Id) return;
      this.progressText = event.Message || event.message; this.processedRows = event.ProcessedRows || event.processedRows || 0; this.totalRows = event.TotalRows || event.totalRows || 0;
      this.refreshSelected();
      this.cdr.markForCheck();
    });
    this.hub.start().then(() => { if (this.selected) return this.hub.invoke('Subscribe', this.selected.Id); }).catch(() => undefined);
  }

  private getErrorMessage(error: any, fallback: string): string {
    if (error?.status >= 500) return fallback;
    if (typeof error?.error === 'string' && error.error.trim()) return error.error;
    return error?.error?.detail || error?.error?.title || fallback;
  }
}
